<template>
  <transition name="dialog">
    <div
      class="modal-wrapper"
      ref="dialogWrapperRef"
      v-show="visibleDialog"
      @click.self="handleWrapperClick"
      @mousewheel.prevent.stop
    >
      <div
        ref="dialogRef"
        :class="['default-dialog', customClass, {'fullscreen' : fullscreen}]"
        v-if="dialogRender"
        @mousewheel.passive.stop
      >
        <div style="display:flex;flex-direction:row;justify-content:space-between;">
          <span style="margin-left: 10px;font-size: 18px;">{{title}}</span>
          <button
            type="button"
            class="close-btn"
            aria-label="close"
            v-if="displayClose"
            @click="handleClose"
          ></button>
        </div>
        
        <!-- 对话框主体 -->
        <div class="modal-body">         
          <!-- 默认插槽 -->
          <slot></slot>
        </div>
        <div style="display: flex;flex-direction: row;justify-content: flex-end;">
          <slot name="footer"></slot>
        </div>        
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { nextTick, watch, ref } from 'vue';

const visibleDialog = ref(false);
const dialogRender = ref(false);
const dialogWrapperRef = ref<HTMLElement | null>(null);
const dialogRef = ref<HTMLElement | null>(null);

// 采用定义props接口
interface Props {
  modelValue?: boolean;
  title?: string;
  // 关闭弹窗前的回调（接收一个参数 done()）
  beforeClose?: () => {};
  modal?: boolean;
  lockScroll?: boolean;
  closeOnClickModal?: boolean | string;
  displayClose?: boolean;
  // 最大宽高
  width?: string;
  height?: string;
  top?: string;
  dark?: boolean;
  customClass?: string;
  fullscreen: boolean;
}

// 采用ts专有声明，有默认值
const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  title: '',
  // 是否需要遮罩层
  modal: true,
  // 是否在 Dialog 出现时将 body 滚动锁定
  lockScroll: true,
  // 是否可以通过点击 modal 关闭 Dialog
  closeOnClickModal: false,
  // 是否显示右上角关闭按钮
  displayClose: true,
  top: '5vh',
  // 主题颜色 - 高亮（默认） | 夜间
  dark: false,
  // 自定义类
  customClass: '',
  fullscreen: false
});

// 解构props
const {
  modelValue,
  title,
  beforeClose,
  modal,
  lockScroll,
  closeOnClickModal,
  displayClose,
  width,
  height,
  top,
  dark,
  customClass,
} = props;

// 定义emit方法名称
const emit = defineEmits(['close', 'open', 'update:modelValue']);

// 监听visible改变
watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      visibleDialog.value = true;
      dialogRender.value = true;
      // 依据props修改样式
      changeDialogStyle();
      emit('open');
    } else {
      visibleDialog.value = false;
      dialogRender.value = false;
      emit('close');
    }
  }
);

const handleWrapperClick = () => {
  if (typeof closeOnClickModal == 'boolean' && closeOnClickModal) {
    handleClose();
  }
};

// 处理关闭对话框，若存在beforeClose则调用
const handleClose = () => {
  if (typeof beforeClose === 'function') {
    beforeClose(updateVisible);
  } else {
    updateVisible();
  }
};

// 通知更新visible = false
const updateVisible = () => {
  emit('update:modelValue', false);
};

// 根据Props值修改Dialog样式
const changeDialogStyle = async () => {
  // lockScroll - 实现底层禁止滚动
  // if (this.lockScroll) {
  //   document.body.style['overflow'] = 'hidden'
  // }
  await nextTick(()=> {
    const dialogWrapperStyle = dialogWrapperRef.value.style;
    const dialogStyle = dialogRef.value.style;
    if (width) {
      dialogStyle.width = width;
    }
    if (height) {
      dialogStyle.height = height;
    }
    // if (top) {
    //   dialogStyle.marginTop = that.top
    // }
    // 实现无遮罩层
    if (!modal) {
      dialogWrapperStyle.background = 'transparent';
    }
  });
};
</script>

<style lang="scss" scoped>
.modal-wrapper {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 100;

  // 默认样式
  .default-dialog {
    position: absolute;
    border-radius: 5px;
    color: #303952;
    padding: 20px;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 400px;
    min-height: 200px;
    background-color: #fff;

    .close-btn {
        position: absolute;
        right: 10px;
        top: 10px;
        height: 20px;
        width: 20px;
        background-color: transparent;
        border-color: transparent;
        padding: 0;
        outline: none;
        cursor: pointer;
        text-align: center;
        z-index: 1;
        font-size: 0px;
        display: block;
        box-sizing: content-box;
        &::before,
        &::after {
          content: '';
          width: 20px;
          height: 2px;
          background: gray;
          display: block;
          transition: all 0.25s ease-in-out;
        }
        &::before {
          transform: rotate(45deg);
        }
        &::after {
          transform: translateY(-2px) rotate(-45deg);
        }
        &:hover::before,
        &:hover::after {
          background: #0a59f7;
        }
      }

    .modal-body {
      height: 100%;
      position: relative;
      overflow-x: scroll;
    }
  }
}

.fullscreen {
  width: 100vw;
  height: 100vh;
}

/* 命名过渡动画 */
.dialog-enter-active,
.dialog-leave-active {
  transition: all 0.3s ease;
}
.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}
</style>
